import { useMemo, useCallback } from 'react';
import {
  Input,
  Select,
  SimpleGrid,
  VStack,
  Box,
  Text,
  Checkbox,
  Divider,
  Button,
  useDisclosure,
  HStack,
  Icon,
} from '@chakra-ui/react';
import { WarningIcon } from '@chakra-ui/icons';

import DateField from 'components/fields/DateField';
import { parseDateString, formatDateToString } from 'utils/helpers';
import { SectionCard, ValidatedField } from '../../components';
import {
  useAnswerCodes,
  useValidationSchema,
  ValidationEntityProvider,
} from '../../hooks';
import type { Location, OilTank, PlumbingMaterial } from 'api';
import { createEmptyLocationCoverages } from '../../constants/habitationalCoveragesOptions';
import AuxiliaryHeatingModal from '../../components/AuxiliaryHeatingModal';
import OilTanksModal from '../../components/OilTanksModal';
import PlumbingMaterialsInput from '../../components/PlumbingMaterialsInput';

interface CombinedDetailsCardProps {
  location: Location;
  locationIndex: number;
  onUpdate: (index: number, field: keyof Location, value: unknown) => void;
  isUpdating?: boolean;
  height?: string;
}

interface ColumnProps {
  location: Location;
  locationIndex: number;
  onUpdate: (index: number, field: keyof Location, value: unknown) => void;
  isUpdating?: boolean;
}

type ExtentField =
  | 'extent_of_updates_to_electrical'
  | 'extent_of_updates_to_plumbing'
  | 'extent_of_updates_to_roof'
  | 'extent_of_updates_to_heating';

const createExtentHandler =
  (
    locationIndex: number,
    field: ExtentField,
    onUpdate: (index: number, field: keyof Location, value: unknown) => void,
  ) =>
  (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (val === '') {
      onUpdate(locationIndex, field, '');
      return;
    }
    const num = parseInt(val, 10);
    if (!isNaN(num) && num >= 0 && num <= 100) {
      onUpdate(locationIndex, field, val);
    }
  };

const ELECTRICAL_SERVICE_OPTIONS = [
  { value: '', label: 'Select...' },
  { value: 60, label: '60 Amp' },
  { value: 100, label: '100 Amp' },
  { value: 200, label: '200 Amp' },
  { value: 400, label: '400 Amp' },
];

const ElectricalColumn = ({
  location,
  locationIndex,
  onUpdate,
  isUpdating,
}: ColumnProps) => {
  const { getOptions, shouldShowConditionalField } = useAnswerCodes();

  const showOtherWiringType = shouldShowConditionalField(
    'other_wiring_type',
    location.wiring_type,
  );

  const handleSelectChange =
    (field: keyof Location, otherField?: keyof Location) =>
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      onUpdate(locationIndex, field, e.target.value);
      if (
        otherField &&
        !shouldShowConditionalField(
          otherField as Parameters<typeof shouldShowConditionalField>[0],
          e.target.value,
        )
      ) {
        onUpdate(locationIndex, otherField, '');
      }
    };

  const handleTextChange =
    (field: keyof Location) => (e: React.ChangeEvent<HTMLInputElement>) => {
      onUpdate(locationIndex, field, e.target.value);
    };

  const handleNumberChange =
    (field: keyof Location) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value === '' ? null : parseInt(e.target.value, 10);
      onUpdate(locationIndex, field, isNaN(value as number) ? null : value);
    };

  const handleServiceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value === '' ? null : parseInt(e.target.value, 10);
    onUpdate(locationIndex, 'electrical_strength_amperes_a', value);
  };

  return (
    <VStack spacing={3} align="stretch">
      <Text fontWeight="semibold" fontSize="sm" color="gray.600">
        Electrical Details
      </Text>

      <SimpleGrid columns={1} spacing={2}>
        <ValidatedField
          itemPath="location"
          fieldPath="year_of_most_recent_updates_to_electrical"
          label="Year"
          isDisabled={isUpdating}
          labelSize="xs"
        >
          <Input
            variant="main"
            size="sm"
            type="number"
            min={1900}
            max={new Date().getFullYear()}
            value={location.year_of_most_recent_updates_to_electrical ?? ''}
            onChange={handleNumberChange(
              'year_of_most_recent_updates_to_electrical',
            )}
            isDisabled={isUpdating}
          />
        </ValidatedField>

        <ValidatedField
          itemPath="location"
          fieldPath="extent_of_updates_to_electrical"
          label="% Update Completed"
          isDisabled={isUpdating}
          labelSize="xs"
        >
          <Input
            variant="main"
            size="sm"
            type="number"
            min={0}
            max={100}
            value={location.extent_of_updates_to_electrical || ''}
            onChange={createExtentHandler(
              locationIndex,
              'extent_of_updates_to_electrical',
              onUpdate,
            )}
            isDisabled={isUpdating}
            placeholder="0-100"
          />
        </ValidatedField>
      </SimpleGrid>

      <ValidatedField
        itemPath="location"
        fieldPath="wiring_type"
        label="Type"
        isDisabled={isUpdating}
        labelSize="xs"
      >
        <Select
          variant="main"
          size="sm"
          value={location.wiring_type || ''}
          onChange={handleSelectChange('wiring_type', 'other_wiring_type')}
          isDisabled={isUpdating}
        >
          {getOptions('wiring_type').map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Select>
      </ValidatedField>

      {showOtherWiringType && (
        <ValidatedField
          itemPath="location"
          fieldPath="other_wiring_type"
          label="Specify Wiring Type"
          isDisabled={isUpdating}
          labelSize="xs"
        >
          <Input
            variant="main"
            size="sm"
            value={location.other_wiring_type || ''}
            onChange={handleTextChange('other_wiring_type')}
            isDisabled={isUpdating}
          />
        </ValidatedField>
      )}

      <ValidatedField
        itemPath="location"
        fieldPath="electrical_strength_amperes_a"
        label="Service"
        isDisabled={isUpdating}
        labelSize="xs"
      >
        <Select
          variant="main"
          size="sm"
          value={location.electrical_strength_amperes_a ?? ''}
          onChange={handleServiceChange}
          isDisabled={isUpdating}
        >
          {ELECTRICAL_SERVICE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Select>
      </ValidatedField>

      <ValidatedField
        itemPath="location"
        fieldPath="electrical_panel_type"
        label="Panel"
        isDisabled={isUpdating}
        labelSize="xs"
      >
        <Select
          variant="main"
          size="sm"
          value={location.electrical_panel_type || ''}
          onChange={handleSelectChange('electrical_panel_type')}
          isDisabled={isUpdating}
        >
          {getOptions('electrical_panel_type').map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Select>
      </ValidatedField>
    </VStack>
  );
};

const PlumbingColumn = ({
  location,
  locationIndex,
  onUpdate,
  isUpdating,
}: ColumnProps) => {
  const { getOptions, getLabel, shouldShowConditionalField } = useAnswerCodes();

  const isFieldLocked = useCallback(
    (field: string) => location.itv_locked_fields?.includes(field) ?? false,
    [location.itv_locked_fields],
  );

  const isNoneValue = (code: string): boolean => {
    if (!code) return true;
    const label = getLabel(code, 'hot_water_tank_boiler_type');
    return label.toLowerCase() === 'none';
  };

  const plumbingMaterials = useMemo((): PlumbingMaterial[] => {
    if (location.plumbing_materials && location.plumbing_materials.length > 0) {
      return location.plumbing_materials;
    }
    if (location.plumbing_material_type) {
      return [
        {
          type: location.plumbing_material_type,
          percentage: location.plumbing_material_percentage,
        },
      ];
    }
    return [{ type: '', percentage: null }];
  }, [
    location.plumbing_materials,
    location.plumbing_material_type,
    location.plumbing_material_percentage,
  ]);

  const handlePlumbingMaterialsChange = useCallback(
    (materials: PlumbingMaterial[]) => {
      onUpdate(locationIndex, 'plumbing_materials', materials);
      // Always sync first material to legacy fields for validation
      onUpdate(
        locationIndex,
        'plumbing_material_type',
        materials[0]?.type || '',
      );
      onUpdate(
        locationIndex,
        'plumbing_material_percentage',
        materials[0]?.percentage ?? 100,
      );
    },
    [locationIndex, onUpdate],
  );

  const handleOtherPlumbingChange = useCallback(
    (value: string) => {
      onUpdate(locationIndex, 'other_plumbing_material_type', value);
    },
    [locationIndex, onUpdate],
  );

  const showOtherHotWaterTank = shouldShowConditionalField(
    'other_hot_water_tank_boiler_type',
    location.hot_water_tank_boiler_type,
  );
  const showOtherSumpPumpType = shouldShowConditionalField(
    'other_sump_pump_type',
    location.sump_pump_type,
  );
  const hasSumpPump = shouldShowConditionalField(
    'alarmed_sump_pump',
    location.sump_pump_type,
  );
  const isOtherAuxiliaryPower = shouldShowConditionalField(
    'other_sump_pump_auxiliary_power',
    location.sump_pump_auxiliary_power,
  );
  const showWaterLeakSensors = shouldShowConditionalField(
    'number_of_water_main_valve_shut_off_sensors',
    location.water_leak_detection_system,
  );

  const updateSumpPumpCoverages = (
    hasSumpPump: boolean,
    isAlarmedYes: boolean,
  ) => {
    const currentCoverages =
      location.coverages || createEmptyLocationCoverages();
    onUpdate(locationIndex, 'coverages', {
      ...currentCoverages,
      sump_pump_pit_enabled: hasSumpPump,
      alarmed_sump_pump_enabled: hasSumpPump && isAlarmedYes,
    });
  };

  const isAlarmedYesValue = (value: string): boolean => {
    if (!value) return false;
    const label = getLabel(value, 'alarmed_sump_pump');
    return label.toLowerCase() === 'yes';
  };

  const handleSelectChange =
    (field: keyof Location, otherField?: keyof Location) =>
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      onUpdate(locationIndex, field, e.target.value);
      if (
        otherField &&
        !shouldShowConditionalField(
          otherField as Parameters<typeof shouldShowConditionalField>[0],
          e.target.value,
        )
      ) {
        onUpdate(locationIndex, otherField, '');
      }
      if (
        field === 'hot_water_tank_boiler_type' &&
        isNoneValue(e.target.value)
      ) {
        onUpdate(locationIndex, 'hot_water_tank_boiler_year', null);
      }
      if (
        field === 'water_leak_detection_system' &&
        !shouldShowConditionalField(
          'number_of_water_main_valve_shut_off_sensors',
          e.target.value,
        )
      ) {
        onUpdate(
          locationIndex,
          'number_of_water_main_valve_shut_off_sensors',
          null,
        );
      }
      if (field === 'sump_pump_type') {
        const newHasSumpPump = shouldShowConditionalField(
          'alarmed_sump_pump',
          e.target.value,
        );
        if (!newHasSumpPump) {
          onUpdate(locationIndex, 'alarmed_sump_pump', '');
          onUpdate(locationIndex, 'sump_pump_auxiliary_power', '');
          onUpdate(locationIndex, 'other_sump_pump_auxiliary_power', '');
          updateSumpPumpCoverages(false, false);
        } else {
          const currentAlarmedYes = isAlarmedYesValue(
            location.alarmed_sump_pump || '',
          );
          updateSumpPumpCoverages(true, currentAlarmedYes);
        }
      }
      if (field === 'alarmed_sump_pump') {
        const currentHasSumpPump = shouldShowConditionalField(
          'alarmed_sump_pump',
          location.sump_pump_type || '',
        );
        if (currentHasSumpPump) {
          const newIsAlarmedYes = isAlarmedYesValue(e.target.value);
          updateSumpPumpCoverages(true, newIsAlarmedYes);
        }
      }
    };

  const handleTextChange =
    (field: keyof Location) => (e: React.ChangeEvent<HTMLInputElement>) => {
      onUpdate(locationIndex, field, e.target.value);
    };

  const handleNumberChange =
    (field: keyof Location) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value === '' ? null : parseInt(e.target.value, 10);
      onUpdate(locationIndex, field, isNaN(value as number) ? null : value);
    };

  const handleCheckboxChange =
    (field: keyof Location) => (e: React.ChangeEvent<HTMLInputElement>) => {
      onUpdate(locationIndex, field, e.target.checked);
    };

  return (
    <VStack spacing={3} align="stretch">
      <Text fontWeight="semibold" fontSize="sm" color="gray.600">
        Plumbing Details
      </Text>

      <SimpleGrid columns={1} spacing={2}>
        <ValidatedField
          itemPath="location"
          fieldPath="year_of_most_recent_updates_to_plumbing"
          label="Year"
          isDisabled={isUpdating}
          labelSize="xs"
        >
          <Input
            variant="main"
            size="sm"
            type="number"
            min={1900}
            max={new Date().getFullYear()}
            value={location.year_of_most_recent_updates_to_plumbing ?? ''}
            onChange={handleNumberChange(
              'year_of_most_recent_updates_to_plumbing',
            )}
            isDisabled={isUpdating}
          />
        </ValidatedField>

        <ValidatedField
          itemPath="location"
          fieldPath="extent_of_updates_to_plumbing"
          label="% Update Completed"
          isDisabled={isUpdating}
          labelSize="xs"
        >
          <Input
            variant="main"
            size="sm"
            type="number"
            min={0}
            max={100}
            value={location.extent_of_updates_to_plumbing || ''}
            onChange={createExtentHandler(
              locationIndex,
              'extent_of_updates_to_plumbing',
              onUpdate,
            )}
            isDisabled={isUpdating}
            placeholder="0-100"
          />
        </ValidatedField>
      </SimpleGrid>

      <ValidatedField
        itemPath="location"
        fieldPath="plumbing_material_type"
        label="Type"
        isDisabled={isUpdating || isFieldLocked('plumbing_material_type')}
        labelSize="xs"
      >
        <PlumbingMaterialsInput
          value={plumbingMaterials[0]?.type || ''}
          materials={plumbingMaterials}
          onChange={handlePlumbingMaterialsChange}
          otherPlumbingMaterialType={
            location.other_plumbing_material_type || ''
          }
          onOtherChange={handleOtherPlumbingChange}
          isDisabled={isUpdating}
          isFieldLocked={isFieldLocked}
        />
      </ValidatedField>

      <ValidatedField
        itemPath="location"
        fieldPath="hot_water_tank_boiler_year"
        label="Water Heater Year"
        isDisabled={isUpdating}
        labelSize="xs"
      >
        <Input
          variant="main"
          size="sm"
          type="number"
          min={1900}
          max={new Date().getFullYear()}
          value={location.hot_water_tank_boiler_year ?? ''}
          onChange={handleNumberChange('hot_water_tank_boiler_year')}
          isDisabled={isUpdating}
        />
      </ValidatedField>

      <ValidatedField
        itemPath="location"
        fieldPath="hot_water_tank_boiler_type"
        label="Water Heater"
        isDisabled={isUpdating}
        labelSize="xs"
      >
        <Select
          variant="main"
          size="sm"
          value={location.hot_water_tank_boiler_type || ''}
          onChange={handleSelectChange(
            'hot_water_tank_boiler_type',
            'other_hot_water_tank_boiler_type',
          )}
          isDisabled={isUpdating}
        >
          {getOptions('hot_water_tank_boiler_type').map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Select>
      </ValidatedField>

      {showOtherHotWaterTank && (
        <ValidatedField
          itemPath="location"
          fieldPath="other_hot_water_tank_boiler_type"
          label="Specify Type"
          isDisabled={isUpdating}
          labelSize="xs"
        >
          <Input
            variant="main"
            size="sm"
            value={location.other_hot_water_tank_boiler_type || ''}
            onChange={handleTextChange('other_hot_water_tank_boiler_type')}
            isDisabled={isUpdating}
          />
        </ValidatedField>
      )}

      <ValidatedField
        itemPath="location"
        fieldPath="water_main_valve_shut_off_type"
        label="Water Main Valve Shutoff Type"
        isDisabled={isUpdating}
        labelSize="xs"
      >
        <Select
          variant="main"
          size="sm"
          value={location.water_main_valve_shut_off_type || ''}
          onChange={handleSelectChange('water_main_valve_shut_off_type')}
          isDisabled={isUpdating}
        >
          {getOptions('water_main_valve_shut_off_type').map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Select>
      </ValidatedField>

      <ValidatedField
        itemPath="location"
        fieldPath="backwater_valve_type_installed"
        label="Backwater Valve Type"
        isDisabled={isUpdating}
        labelSize="xs"
      >
        <Select
          variant="main"
          size="sm"
          value={location.backwater_valve_type_installed || ''}
          onChange={handleSelectChange('backwater_valve_type_installed')}
          isDisabled={isUpdating}
        >
          {getOptions('backwater_valve_type_installed').map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Select>
      </ValidatedField>

      <ValidatedField
        itemPath="location"
        fieldPath="sump_pump_type"
        label="Sump Pump Type"
        isDisabled={isUpdating}
        labelSize="xs"
      >
        <Select
          variant="main"
          size="sm"
          value={location.sump_pump_type || ''}
          onChange={handleSelectChange(
            'sump_pump_type',
            'other_sump_pump_type',
          )}
          isDisabled={isUpdating}
        >
          {getOptions('sump_pump_type').map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Select>
      </ValidatedField>

      {showOtherSumpPumpType && (
        <ValidatedField
          itemPath="location"
          fieldPath="other_sump_pump_type"
          label="Specify Sump Pump Type"
          isDisabled={isUpdating}
          labelSize="xs"
        >
          <Input
            variant="main"
            size="sm"
            value={location.other_sump_pump_type || ''}
            onChange={handleTextChange('other_sump_pump_type')}
            isDisabled={isUpdating}
          />
        </ValidatedField>
      )}

      {hasSumpPump && (
        <>
          <ValidatedField
            itemPath="location"
            fieldPath="alarmed_sump_pump"
            label="Alarmed"
            isDisabled={isUpdating}
            labelSize="xs"
          >
            <Select
              variant="main"
              size="sm"
              value={location.alarmed_sump_pump || ''}
              onChange={handleSelectChange('alarmed_sump_pump')}
              isDisabled={isUpdating}
            >
              {getOptions('alarmed_sump_pump').map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </ValidatedField>

          <ValidatedField
            itemPath="location"
            fieldPath="sump_pump_auxiliary_power"
            label="Auxiliary Power"
            isDisabled={isUpdating}
            labelSize="xs"
          >
            <Select
              variant="main"
              size="sm"
              value={location.sump_pump_auxiliary_power || ''}
              onChange={handleSelectChange(
                'sump_pump_auxiliary_power',
                'other_sump_pump_auxiliary_power',
              )}
              isDisabled={isUpdating}
            >
              {getOptions('sump_pump_auxiliary_power').map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </ValidatedField>

          {isOtherAuxiliaryPower && (
            <ValidatedField
              itemPath="location"
              fieldPath="other_sump_pump_auxiliary_power"
              label="Specify Auxiliary Power"
              isDisabled={isUpdating}
              labelSize="xs"
            >
              <Input
                variant="main"
                size="sm"
                value={location.other_sump_pump_auxiliary_power || ''}
                onChange={handleTextChange('other_sump_pump_auxiliary_power')}
                isDisabled={isUpdating}
              />
            </ValidatedField>
          )}
        </>
      )}

      <ValidatedField
        itemPath="location"
        fieldPath="water_leak_detection_system"
        label="Water Leak Detection"
        isDisabled={isUpdating}
        labelSize="xs"
      >
        <Select
          variant="main"
          size="sm"
          value={location.water_leak_detection_system || ''}
          onChange={handleSelectChange('water_leak_detection_system')}
          isDisabled={isUpdating}
        >
          {getOptions('water_leak_detection_system').map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Select>
      </ValidatedField>

      {showWaterLeakSensors && (
        <ValidatedField
          itemPath="location"
          fieldPath="number_of_water_main_valve_shut_off_sensors"
          label="Number of Water-Leak Sensors"
          isDisabled={isUpdating}
          labelSize="xs"
        >
          <Input
            variant="main"
            size="sm"
            type="number"
            min={0}
            value={location.number_of_water_main_valve_shut_off_sensors ?? ''}
            onChange={handleNumberChange(
              'number_of_water_main_valve_shut_off_sensors',
            )}
            isDisabled={isUpdating}
          />
        </ValidatedField>
      )}

      <Checkbox
        isChecked={location.septic_tank ?? false}
        onChange={handleCheckboxChange('septic_tank')}
        isDisabled={isUpdating}
        size="sm"
      >
        Septic System
      </Checkbox>
    </VStack>
  );
};

const RoofingColumn = ({
  location,
  locationIndex,
  onUpdate,
  isUpdating,
}: ColumnProps) => {
  const { getOptions, shouldShowConditionalField } = useAnswerCodes();

  const isFieldLocked = (field: string) =>
    location.itv_locked_fields?.includes(field) ?? false;

  const showOwnerRoofing = shouldShowConditionalField(
    'roofing_material_type',
    location.location_type,
  );
  const showTenantCondoRoofing = shouldShowConditionalField(
    'tenant_condo_roofing_material_type',
    location.location_type,
  );

  const showOtherRoofingMaterial = shouldShowConditionalField(
    'other_roofing_material_type',
    location.roofing_material_type,
  );

  const handleSelectChange =
    (field: keyof Location, otherField?: keyof Location) =>
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      onUpdate(locationIndex, field, e.target.value);
      if (
        otherField &&
        !shouldShowConditionalField(
          otherField as Parameters<typeof shouldShowConditionalField>[0],
          e.target.value,
        )
      ) {
        onUpdate(locationIndex, otherField, '');
      }
    };

  const handleTextChange =
    (field: keyof Location) => (e: React.ChangeEvent<HTMLInputElement>) => {
      onUpdate(locationIndex, field, e.target.value);
    };

  const handleNumberChange =
    (field: keyof Location) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value === '' ? null : parseInt(e.target.value, 10);
      onUpdate(locationIndex, field, isNaN(value as number) ? null : value);
    };

  return (
    <VStack spacing={3} align="stretch">
      <Text fontWeight="semibold" fontSize="sm" color="gray.600">
        Roofing Details
      </Text>

      <SimpleGrid columns={1} spacing={2}>
        <ValidatedField
          itemPath="location"
          fieldPath="year_of_most_recent_updates_to_roof"
          label="Year"
          isDisabled={isUpdating}
          labelSize="xs"
        >
          <Input
            variant="main"
            size="sm"
            type="number"
            min={1900}
            max={new Date().getFullYear()}
            value={location.year_of_most_recent_updates_to_roof ?? ''}
            onChange={handleNumberChange('year_of_most_recent_updates_to_roof')}
            isDisabled={isUpdating}
          />
        </ValidatedField>

        <ValidatedField
          itemPath="location"
          fieldPath="extent_of_updates_to_roof"
          label="% Update Completed"
          isDisabled={isUpdating}
          labelSize="xs"
        >
          <Input
            variant="main"
            size="sm"
            type="number"
            min={0}
            max={100}
            value={location.extent_of_updates_to_roof || ''}
            onChange={createExtentHandler(
              locationIndex,
              'extent_of_updates_to_roof',
              onUpdate,
            )}
            isDisabled={isUpdating}
            placeholder="0-100"
          />
        </ValidatedField>
      </SimpleGrid>

      {showOwnerRoofing && (
        <ValidatedField
          itemPath="location"
          fieldPath="roofing_material_type"
          label="Type"
          isDisabled={isUpdating || isFieldLocked('roofing_material_type')}
          labelSize="xs"
        >
          <Select
            variant="main"
            size="sm"
            value={location.roofing_material_type || ''}
            onChange={handleSelectChange(
              'roofing_material_type',
              'other_roofing_material_type',
            )}
            isDisabled={isUpdating || isFieldLocked('roofing_material_type')}
          >
            {getOptions('roofing_material_type').map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        </ValidatedField>
      )}

      {showTenantCondoRoofing && (
        <ValidatedField
          itemPath="location"
          fieldPath="tenant_condo_roofing_material_type"
          label="Type"
          isDisabled={isUpdating}
          labelSize="xs"
        >
          <Select
            variant="main"
            size="sm"
            value={location.tenant_condo_roofing_material_type || ''}
            onChange={handleSelectChange('tenant_condo_roofing_material_type')}
            isDisabled={isUpdating}
          >
            {getOptions('tenant_condo_roofing_material_type').map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        </ValidatedField>
      )}

      {showOwnerRoofing && showOtherRoofingMaterial && (
        <ValidatedField
          itemPath="location"
          fieldPath="other_roofing_material_type"
          label="Specify Material"
          isDisabled={isUpdating}
          labelSize="xs"
        >
          <Input
            variant="main"
            size="sm"
            value={location.other_roofing_material_type || ''}
            onChange={handleTextChange('other_roofing_material_type')}
            isDisabled={isUpdating}
          />
        </ValidatedField>
      )}

      {shouldShowConditionalField(
        'impact_resistance_class_of_roofing',
        location as unknown as Record<string, unknown>,
      ) && (
        <ValidatedField
          itemPath="location"
          fieldPath="impact_resistance_class_of_roofing"
          label="Impact Resistance"
          isDisabled={isUpdating}
          labelSize="xs"
        >
          <Select
            variant="main"
            size="sm"
            value={location.impact_resistance_class_of_roofing || ''}
            onChange={handleSelectChange('impact_resistance_class_of_roofing')}
            isDisabled={isUpdating}
          >
            {getOptions('impact_resistance_class_of_roofing').map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        </ValidatedField>
      )}
    </VStack>
  );
};

interface HeatingColumnProps extends ColumnProps {
  onOpenAuxiliaryModal: () => void;
  onOpenOilTanksModal: () => void;
  primaryOilTanksCount: number;
  isPrimaryOilTanksIncomplete: boolean;
}

const HeatingColumn = ({
  location,
  locationIndex,
  onUpdate,
  isUpdating,
  onOpenAuxiliaryModal,
  onOpenOilTanksModal,
  primaryOilTanksCount,
  isPrimaryOilTanksIncomplete,
}: HeatingColumnProps) => {
  const {
    getOptions,
    getLabel,
    shouldShowConditionalField,
    shouldShowConditionalFieldForHeatingSource,
    getValidFuelOptions,
    answerCodes,
    isHeatingTypeNone,
  } = useAnswerCodes();
  const { isFieldRequired } = useValidationSchema();

  const isFieldLocked = (field: string) =>
    location.itv_locked_fields?.includes(field) ?? false;

  const isAuxiliaryHeatingRequired =
    isFieldRequired(
      'location',
      'auxiliary_heating_type_installed',
      location as unknown as Record<string, unknown>,
    ) && !location.auxiliary_heating_type_installed;

  const isAuxiliaryHeatingAllowed =
    location.primary_heating_type_installed &&
    !isHeatingTypeNone(location.primary_heating_type_installed);

  const showOtherHeatingType = shouldShowConditionalField(
    'other_primary_heating_type',
    location.primary_heating_type_installed,
  );
  const showFuelType =
    location.primary_heating_type_installed &&
    !isHeatingTypeNone(location.primary_heating_type_installed);
  const showOtherFuelType = shouldShowConditionalField(
    'other_primary_heating_fuel_type',
    location.primary_heating_fuel_type,
  );
  const showChimneyExists = shouldShowConditionalFieldForHeatingSource(
    'chimney_exists',
    location as unknown as Record<string, unknown>,
    'primary',
  );
  const showPrimaryOilTanks = shouldShowConditionalFieldForHeatingSource(
    'number_of_oil_storage_tanks',
    location as unknown as Record<string, unknown>,
    'primary',
  );
  const isChimneyTypeOther = (): boolean => {
    if (!location.chimney_type) return false;
    const label = getLabel(location.chimney_type, 'chimney_type');
    return label.toLowerCase() === 'other';
  };

  const chimneyExists = (): boolean => {
    if (!location.chimney_exists) return false;
    const label = getLabel(location.chimney_exists, 'chimney_exists');
    return label.toLowerCase() === 'yes';
  };

  const showPrimaryFaceCords = shouldShowConditionalField(
    'primary_heating_fuel_type_number_of_face_cords_per_year',
    location as unknown as Record<string, unknown>,
  );

  const showPrimaryApprovedBy = shouldShowConditionalField(
    'primary_heating_approved_by',
    location.primary_heating_type_installed,
  );
  const showPrimaryProfessionallyInstalled = shouldShowConditionalField(
    'primary_heating_professionally_installed',
    location.primary_heating_type_installed,
  );
  const showPrimaryFireplaceType = shouldShowConditionalField(
    'primary_heating_fireplace_type',
    location.primary_heating_type_installed,
  );
  const showSecondPrimaryFuelType = shouldShowConditionalField(
    'second_primary_heating_fuel_type',
    location.primary_heating_type_installed,
  );
  const showPrimaryWettCertified = shouldShowConditionalField(
    'primary_heating_wett_certified',
    location as unknown as Record<string, unknown>,
  );
  const showPrimaryWettDetails = shouldShowConditionalField(
    'primary_heating_wett_certification_number',
    location.primary_heating_wett_certified,
  );
  const showChimneyBuiltFrom = shouldShowConditionalField(
    'chimney_built_from',
    location.chimney_exists,
  );
  const showChimneyLining = shouldShowConditionalField(
    'chimney_lining',
    location.chimney_exists,
  );
  const showChimneyLocation = shouldShowConditionalField(
    'chimney_location',
    location.chimney_exists,
  );
  const showChimneyProfessionallyCleaned = shouldShowConditionalField(
    'chimney_professionally_cleaned',
    location.chimney_exists,
  );
  const showFrequencyOfChimneyCleaning = shouldShowConditionalField(
    'frequency_of_chimney_cleaning',
    location.chimney_exists,
  );
  const showWoodStoveFields = shouldShowConditionalFieldForHeatingSource(
    'stove_location',
    location as unknown as Record<string, unknown>,
    'primary',
  );

  const handleSelectChange =
    (field: keyof Location, otherField?: keyof Location) =>
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      onUpdate(locationIndex, field, e.target.value);
      if (
        otherField &&
        !shouldShowConditionalField(
          otherField as Parameters<typeof shouldShowConditionalField>[0],
          e.target.value,
        )
      ) {
        onUpdate(locationIndex, otherField, '');
      }
      if (field === 'primary_heating_type_installed') {
        if (isHeatingTypeNone(e.target.value)) {
          onUpdate(locationIndex, 'primary_heating_fuel_type', '');
          onUpdate(locationIndex, 'other_primary_heating_fuel_type', '');
          onUpdate(
            locationIndex,
            'primary_heating_fuel_type_number_of_face_cords_per_year',
            null,
          );
          onUpdate(locationIndex, 'auxiliary_heating_type_installed', '');
          onUpdate(locationIndex, 'other_auxiliary_heating_type', '');
          onUpdate(locationIndex, 'auxiliary_heating_fuel_type', '');
          onUpdate(locationIndex, 'other_auxiliary_heating_fuel_type', '');
          onUpdate(
            locationIndex,
            'auxiliary_heating_fuel_type_number_of_face_cords_per_year',
            null,
          );
          onUpdate(locationIndex, 'auxiliary_heating_approved', false);
          onUpdate(locationIndex, 'auxiliary_heating_year_installed', null);
          onUpdate(
            locationIndex,
            'auxiliary_heating_percentage_renovated',
            null,
          );
          onUpdate(
            locationIndex,
            'auxiliary_heating_automatic_fire_extinguisher',
            false,
          );
          onUpdate(locationIndex, 'number_of_hours_used_per_day', null);
          onUpdate(locationIndex, 'chimney_type', '');
          onUpdate(locationIndex, 'other_chimney_type', '');
          onUpdate(locationIndex, 'oil_tanks', []);
        } else {
          const newHeatingType = e.target.value;
          const validFuels =
            answerCodes?.heating_fuel_mapping?.[newHeatingType];
          const currentFuel = location.primary_heating_fuel_type;

          if (validFuels && currentFuel && !validFuels.includes(currentFuel)) {
            onUpdate(locationIndex, 'primary_heating_fuel_type', '');
            onUpdate(locationIndex, 'other_primary_heating_fuel_type', '');
            onUpdate(
              locationIndex,
              'primary_heating_fuel_type_number_of_face_cords_per_year',
              null,
            );
          }
        }
      }

      const fuelTypeFields = [
        'primary_heating_fuel_type',
        'auxiliary_heating_fuel_type',
        'second_primary_heating_fuel_type',
        'second_auxiliary_heating_fuel_type',
      ];
      if (fuelTypeFields.includes(field)) {
        const updatedLocation = { ...location, [field]: e.target.value };
        const oilTanksStillNeeded = shouldShowConditionalField(
          'number_of_oil_storage_tanks',
          updatedLocation as unknown as Record<string, unknown>,
        );
        if (!oilTanksStillNeeded) {
          onUpdate(locationIndex, 'oil_tanks', []);
        }
      }
    };

  const handleTextChange =
    (field: keyof Location) => (e: React.ChangeEvent<HTMLInputElement>) => {
      onUpdate(locationIndex, field, e.target.value);
    };

  const handleNumberChange =
    (field: keyof Location) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value === '' ? null : parseInt(e.target.value, 10);
      onUpdate(locationIndex, field, isNaN(value as number) ? null : value);
    };

  const handleDateChange = (field: keyof Location) => (date: Date | null) => {
    onUpdate(locationIndex, field, formatDateToString(date));
  };

  return (
    <VStack spacing={3} align="stretch">
      <Text fontWeight="semibold" fontSize="sm" color="gray.600">
        Heating Details
      </Text>

      <SimpleGrid columns={1} spacing={2}>
        <ValidatedField
          itemPath="location"
          fieldPath="year_of_most_recent_updates_to_heating"
          label="Year"
          isDisabled={isUpdating}
          labelSize="xs"
        >
          <Input
            variant="main"
            size="sm"
            type="number"
            min={1900}
            max={new Date().getFullYear()}
            value={location.year_of_most_recent_updates_to_heating ?? ''}
            onChange={handleNumberChange(
              'year_of_most_recent_updates_to_heating',
            )}
            isDisabled={isUpdating}
          />
        </ValidatedField>

        <ValidatedField
          itemPath="location"
          fieldPath="extent_of_updates_to_heating"
          label="% Update Completed"
          isDisabled={isUpdating}
          labelSize="xs"
        >
          <Input
            variant="main"
            size="sm"
            type="number"
            min={0}
            max={100}
            value={location.extent_of_updates_to_heating || ''}
            onChange={createExtentHandler(
              locationIndex,
              'extent_of_updates_to_heating',
              onUpdate,
            )}
            isDisabled={isUpdating}
            placeholder="0-100"
          />
        </ValidatedField>
      </SimpleGrid>

      <ValidatedField
        itemPath="location"
        fieldPath="primary_heating_type_installed"
        label="Type"
        isDisabled={
          isUpdating || isFieldLocked('primary_heating_type_installed')
        }
        labelSize="xs"
      >
        <Select
          variant="main"
          size="sm"
          value={location.primary_heating_type_installed || ''}
          onChange={handleSelectChange(
            'primary_heating_type_installed',
            'other_primary_heating_type',
          )}
          isDisabled={
            isUpdating || isFieldLocked('primary_heating_type_installed')
          }
        >
          {getOptions('primary_heating_type_installed').map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Select>
      </ValidatedField>

      {showOtherHeatingType && (
        <ValidatedField
          itemPath="location"
          fieldPath="other_primary_heating_type"
          label="Specify Type"
          isDisabled={isUpdating}
          labelSize="xs"
        >
          <Input
            variant="main"
            size="sm"
            value={location.other_primary_heating_type || ''}
            onChange={handleTextChange('other_primary_heating_type')}
            isDisabled={isUpdating}
          />
        </ValidatedField>
      )}

      {showFuelType && (
        <>
          <ValidatedField
            itemPath="location"
            fieldPath="primary_heating_fuel_type"
            label="Fuel Type"
            isDisabled={
              isUpdating || isFieldLocked('primary_heating_fuel_type')
            }
            labelSize="xs"
          >
            <Select
              variant="main"
              size="sm"
              value={location.primary_heating_fuel_type || ''}
              onChange={handleSelectChange(
                'primary_heating_fuel_type',
                'other_primary_heating_fuel_type',
              )}
              isDisabled={
                isUpdating || isFieldLocked('primary_heating_fuel_type')
              }
            >
              {getValidFuelOptions(
                location.primary_heating_type_installed || '',
              ).map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </ValidatedField>

          {showOtherFuelType && (
            <ValidatedField
              itemPath="location"
              fieldPath="other_primary_heating_fuel_type"
              label="Specify Fuel"
              isDisabled={isUpdating}
              labelSize="xs"
            >
              <Input
                variant="main"
                size="sm"
                value={location.other_primary_heating_fuel_type || ''}
                onChange={handleTextChange('other_primary_heating_fuel_type')}
                isDisabled={isUpdating}
              />
            </ValidatedField>
          )}

          {showPrimaryFaceCords && (
            <ValidatedField
              itemPath="location"
              fieldPath="primary_heating_fuel_type_number_of_face_cords_per_year"
              label="Bush Cords Annually"
              isDisabled={isUpdating}
              labelSize="xs"
            >
              <Input
                variant="main"
                size="sm"
                type="number"
                min={0}
                value={
                  location.primary_heating_fuel_type_number_of_face_cords_per_year ??
                  ''
                }
                onChange={handleNumberChange(
                  'primary_heating_fuel_type_number_of_face_cords_per_year',
                )}
                isDisabled={isUpdating}
              />
            </ValidatedField>
          )}
        </>
      )}

      {showPrimaryApprovedBy && (
        <ValidatedField
          itemPath="location"
          fieldPath="primary_heating_approved_by"
          label="Approved By"
          isDisabled={isUpdating}
          labelSize="xs"
        >
          <Select
            variant="main"
            size="sm"
            value={location.primary_heating_approved_by || ''}
            onChange={handleSelectChange('primary_heating_approved_by')}
            isDisabled={isUpdating}
          >
            {getOptions('primary_heating_approved_by').map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        </ValidatedField>
      )}

      {showPrimaryProfessionallyInstalled && (
        <ValidatedField
          itemPath="location"
          fieldPath="primary_heating_professionally_installed"
          label="Professionally Installed"
          isDisabled={isUpdating}
          labelSize="xs"
        >
          <Select
            variant="main"
            size="sm"
            value={location.primary_heating_professionally_installed || ''}
            onChange={handleSelectChange(
              'primary_heating_professionally_installed',
            )}
            isDisabled={isUpdating}
          >
            {getOptions('primary_heating_professionally_installed').map(
              (opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ),
            )}
          </Select>
        </ValidatedField>
      )}

      {showPrimaryFireplaceType && (
        <ValidatedField
          itemPath="location"
          fieldPath="primary_heating_fireplace_type"
          label="Fireplace Type"
          isDisabled={isUpdating}
          labelSize="xs"
        >
          <Select
            variant="main"
            size="sm"
            value={location.primary_heating_fireplace_type || ''}
            onChange={handleSelectChange('primary_heating_fireplace_type')}
            isDisabled={isUpdating}
          >
            {getOptions('primary_heating_fireplace_type').map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        </ValidatedField>
      )}

      {showSecondPrimaryFuelType && (
        <ValidatedField
          itemPath="location"
          fieldPath="second_primary_heating_fuel_type"
          label="Second Fuel (Bi-Energy)"
          isDisabled={isUpdating}
          labelSize="xs"
        >
          <Select
            variant="main"
            size="sm"
            value={location.second_primary_heating_fuel_type || ''}
            onChange={handleSelectChange('second_primary_heating_fuel_type')}
            isDisabled={isUpdating}
          >
            {getOptions('second_primary_heating_fuel_type').map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        </ValidatedField>
      )}

      {showPrimaryWettCertified && (
        <ValidatedField
          itemPath="location"
          fieldPath="primary_heating_wett_certified"
          label="WETT Certified"
          isDisabled={isUpdating}
          labelSize="xs"
        >
          <Select
            variant="main"
            size="sm"
            value={location.primary_heating_wett_certified || ''}
            onChange={handleSelectChange('primary_heating_wett_certified')}
            isDisabled={isUpdating}
          >
            {getOptions('primary_heating_wett_certified').map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        </ValidatedField>
      )}

      {showPrimaryWettDetails && (
        <>
          <ValidatedField
            itemPath="location"
            fieldPath="primary_heating_wett_certification_number"
            label="WETT Cert #"
            isDisabled={isUpdating}
            labelSize="xs"
          >
            <Input
              variant="main"
              size="sm"
              value={location.primary_heating_wett_certification_number || ''}
              onChange={handleTextChange(
                'primary_heating_wett_certification_number',
              )}
              isDisabled={isUpdating}
            />
          </ValidatedField>

          <DateField
            label="WETT Inspection Date"
            required={isFieldRequired(
              'location',
              'primary_heating_wett_inspection_date',
              location as unknown as Record<string, unknown>,
            )}
            date={parseDateString(
              location.primary_heating_wett_inspection_date,
            )}
            onChange={handleDateChange('primary_heating_wett_inspection_date')}
            isDisabled={isUpdating}
            maxDate={new Date()}
            size="sm"
          />
        </>
      )}

      {showChimneyExists && (
        <ValidatedField
          itemPath="location"
          fieldPath="chimney_exists"
          label="Chimney Exists"
          isDisabled={isUpdating}
          labelSize="xs"
        >
          <Select
            variant="main"
            size="sm"
            value={location.chimney_exists || ''}
            onChange={handleSelectChange('chimney_exists')}
            isDisabled={isUpdating}
          >
            {getOptions('chimney_exists').map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        </ValidatedField>
      )}

      {chimneyExists() && (
        <ValidatedField
          itemPath="location"
          fieldPath="chimney_type"
          label="Chimney Type"
          isDisabled={isUpdating}
          labelSize="xs"
        >
          <Select
            variant="main"
            size="sm"
            value={location.chimney_type || ''}
            onChange={handleSelectChange('chimney_type', 'other_chimney_type')}
            isDisabled={isUpdating}
          >
            {getOptions('chimney_type').map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        </ValidatedField>
      )}

      {chimneyExists() && isChimneyTypeOther() && (
        <ValidatedField
          itemPath="location"
          fieldPath="other_chimney_type"
          label="Specify Chimney"
          isDisabled={isUpdating}
          labelSize="xs"
        >
          <Input
            variant="main"
            size="sm"
            value={location.other_chimney_type || ''}
            onChange={handleTextChange('other_chimney_type')}
            isDisabled={isUpdating}
          />
        </ValidatedField>
      )}

      {showChimneyBuiltFrom && (
        <ValidatedField
          itemPath="location"
          fieldPath="chimney_built_from"
          label="Chimney Built From"
          isDisabled={isUpdating}
          labelSize="xs"
        >
          <Select
            variant="main"
            size="sm"
            value={location.chimney_built_from || ''}
            onChange={handleSelectChange('chimney_built_from')}
            isDisabled={isUpdating}
          >
            {getOptions('chimney_built_from').map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        </ValidatedField>
      )}

      {showChimneyLining && (
        <ValidatedField
          itemPath="location"
          fieldPath="chimney_lining"
          label="Chimney Lining"
          isDisabled={isUpdating}
          labelSize="xs"
        >
          <Select
            variant="main"
            size="sm"
            value={location.chimney_lining || ''}
            onChange={handleSelectChange('chimney_lining')}
            isDisabled={isUpdating}
          >
            {getOptions('chimney_lining').map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        </ValidatedField>
      )}

      {showChimneyLocation && (
        <ValidatedField
          itemPath="location"
          fieldPath="chimney_location"
          label="Chimney Location"
          isDisabled={isUpdating}
          labelSize="xs"
        >
          <Select
            variant="main"
            size="sm"
            value={location.chimney_location || ''}
            onChange={handleSelectChange('chimney_location')}
            isDisabled={isUpdating}
          >
            {getOptions('chimney_location').map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        </ValidatedField>
      )}

      {showChimneyProfessionallyCleaned && (
        <ValidatedField
          itemPath="location"
          fieldPath="chimney_professionally_cleaned"
          label="Professionally Cleaned"
          isDisabled={isUpdating}
          labelSize="xs"
        >
          <Select
            variant="main"
            size="sm"
            value={location.chimney_professionally_cleaned || ''}
            onChange={handleSelectChange('chimney_professionally_cleaned')}
            isDisabled={isUpdating}
          >
            {getOptions('chimney_professionally_cleaned').map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        </ValidatedField>
      )}

      {showFrequencyOfChimneyCleaning && (
        <ValidatedField
          itemPath="location"
          fieldPath="frequency_of_chimney_cleaning"
          label="Cleaning Frequency"
          isDisabled={isUpdating}
          labelSize="xs"
        >
          <Select
            variant="main"
            size="sm"
            value={location.frequency_of_chimney_cleaning || ''}
            onChange={handleSelectChange('frequency_of_chimney_cleaning')}
            isDisabled={isUpdating}
          >
            {getOptions('frequency_of_chimney_cleaning').map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        </ValidatedField>
      )}

      {showWoodStoveFields && (
        <>
          <ValidatedField
            itemPath="location"
            fieldPath="stove_location"
            label="Stove Location"
            isDisabled={isUpdating}
            labelSize="xs"
          >
            <Select
              variant="main"
              size="sm"
              value={location.stove_location || ''}
              onChange={handleSelectChange('stove_location')}
              isDisabled={isUpdating}
            >
              {getOptions('stove_location').map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </ValidatedField>

          <DateField
            label="Stove Install Date"
            required={isFieldRequired(
              'location',
              'woodstove_installed_date',
              location as unknown as Record<string, unknown>,
            )}
            date={parseDateString(location.woodstove_installed_date)}
            onChange={handleDateChange('woodstove_installed_date')}
            isDisabled={isUpdating}
            maxDate={new Date()}
            size="sm"
          />

          <SimpleGrid columns={2} spacing={2}>
            <ValidatedField
              itemPath="location"
              fieldPath="number_of_hours_used_per_day"
              label="Hours/Day"
              isDisabled={isUpdating}
              labelSize="xs"
            >
              <Input
                variant="main"
                size="sm"
                type="number"
                min={0}
                max={24}
                value={location.number_of_hours_used_per_day ?? ''}
                onChange={handleNumberChange('number_of_hours_used_per_day')}
                isDisabled={isUpdating}
              />
            </ValidatedField>

            <ValidatedField
              itemPath="location"
              fieldPath="number_of_days_used_per_year"
              label="Days/Year"
              isDisabled={isUpdating}
              labelSize="xs"
            >
              <Input
                variant="main"
                size="sm"
                type="number"
                min={0}
                max={365}
                value={location.number_of_days_used_per_year ?? ''}
                onChange={handleNumberChange('number_of_days_used_per_year')}
                isDisabled={isUpdating}
              />
            </ValidatedField>
          </SimpleGrid>
        </>
      )}

      <Divider />

      {showPrimaryOilTanks && (
        <Button
          size="sm"
          variant="outline"
          onClick={onOpenOilTanksModal}
          isDisabled={isUpdating}
          borderColor={isPrimaryOilTanksIncomplete ? 'red.400' : undefined}
          w="full"
        >
          <HStack spacing={2}>
            <Text>
              Oil Tanks
              {primaryOilTanksCount > 0 ? ` (${primaryOilTanksCount})` : ''}
            </Text>
            {isPrimaryOilTanksIncomplete && (
              <Icon as={WarningIcon} color="red.400" boxSize={3} />
            )}
          </HStack>
        </Button>
      )}
      <Button
        size="sm"
        variant="outline"
        onClick={onOpenAuxiliaryModal}
        isDisabled={isUpdating || !isAuxiliaryHeatingAllowed}
        borderColor={isAuxiliaryHeatingRequired ? 'red.600' : undefined}
        borderWidth={isAuxiliaryHeatingRequired ? '2px' : undefined}
        _hover={
          isAuxiliaryHeatingRequired ? { borderColor: 'red.600' } : undefined
        }
        title={
          !isAuxiliaryHeatingAllowed
            ? 'Select a primary heating type first'
            : undefined
        }
      >
        Auxiliary Heating
      </Button>
    </VStack>
  );
};

const CombinedDetailsCard = ({
  location,
  locationIndex,
  onUpdate,
  isUpdating,
  height,
}: CombinedDetailsCardProps) => {
  const { shouldShowConditionalField, getCodeByLabel } = useAnswerCodes();
  const { validateEntity } = useValidationSchema();
  const auxiliaryModal = useDisclosure();
  const oilTanksModal = useDisclosure();

  const primaryAssociationCode = useMemo(
    () => getCodeByLabel('Primary', 'oil_storage_tank_heating_associated_with'),
    [getCodeByLabel],
  );

  const primaryOilTanks = useMemo(() => {
    const tanks = location.oil_tanks || [];
    return tanks.filter(
      (t) => t.heating_associated_with === primaryAssociationCode,
    );
  }, [location.oil_tanks, primaryAssociationCode]);

  const isPrimaryOilTanksIncomplete = useMemo(() => {
    return primaryOilTanks.some(
      (tank) =>
        validateEntity(
          'location/oil_tank',
          tank as unknown as Record<string, unknown>,
        ).length > 0,
    );
  }, [primaryOilTanks, validateEntity]);

  const handleAuxiliarySave = (data: Partial<Location>) => {
    Object.entries(data).forEach(([field, value]) => {
      onUpdate(locationIndex, field as keyof Location, value);
    });

    const updatedLocation = { ...location, ...data };

    const chimneyStillNeeded = shouldShowConditionalField(
      'chimney_exists',
      updatedLocation as unknown as Record<string, unknown>,
    );
    if (!chimneyStillNeeded) {
      onUpdate(locationIndex, 'chimney_exists', '');
      onUpdate(locationIndex, 'chimney_type', '');
      onUpdate(locationIndex, 'other_chimney_type', '');
      onUpdate(locationIndex, 'chimney_built_from', '');
      onUpdate(locationIndex, 'chimney_built_from_other', '');
      onUpdate(locationIndex, 'chimney_lining', '');
      onUpdate(locationIndex, 'chimney_lining_other', '');
      onUpdate(locationIndex, 'chimney_location', '');
      onUpdate(locationIndex, 'chimney_location_other', '');
      onUpdate(locationIndex, 'chimney_professionally_cleaned', '');
      onUpdate(locationIndex, 'frequency_of_chimney_cleaning', '');
    }

    const woodStoveStillNeeded = shouldShowConditionalField(
      'stove_location',
      updatedLocation as unknown as Record<string, unknown>,
    );
    if (!woodStoveStillNeeded) {
      onUpdate(locationIndex, 'stove_location', '');
      onUpdate(locationIndex, 'stove_location_other', '');
      onUpdate(locationIndex, 'woodstove_installed_date', null);
      onUpdate(locationIndex, 'number_of_hours_used_per_day', null);
      onUpdate(locationIndex, 'number_of_days_used_per_year', null);
    }

    auxiliaryModal.onClose();
  };

  const handleOilTanksUpdate = (tanks: OilTank[]) => {
    onUpdate(locationIndex, 'oil_tanks', tanks);
  };

  return (
    <ValidationEntityProvider
      entity={location as unknown as Record<string, unknown>}
    >
      <SectionCard title="Property Details" height={height}>
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
          <Box
            borderRightWidth={{ lg: '1px' }}
            borderColor="gray.200"
            pr={{ lg: 4 }}
          >
            <ElectricalColumn
              location={location}
              locationIndex={locationIndex}
              onUpdate={onUpdate}
              isUpdating={isUpdating}
            />
          </Box>

          <Box
            borderRightWidth={{ lg: '1px' }}
            borderColor="gray.200"
            pr={{ lg: 4 }}
          >
            <PlumbingColumn
              location={location}
              locationIndex={locationIndex}
              onUpdate={onUpdate}
              isUpdating={isUpdating}
            />
          </Box>

          <Box
            borderRightWidth={{ lg: '1px' }}
            borderColor="gray.200"
            pr={{ lg: 4 }}
          >
            <RoofingColumn
              location={location}
              locationIndex={locationIndex}
              onUpdate={onUpdate}
              isUpdating={isUpdating}
            />
          </Box>

          <Box>
            <HeatingColumn
              location={location}
              locationIndex={locationIndex}
              onUpdate={onUpdate}
              isUpdating={isUpdating}
              onOpenAuxiliaryModal={auxiliaryModal.onOpen}
              onOpenOilTanksModal={oilTanksModal.onOpen}
              primaryOilTanksCount={primaryOilTanks.length}
              isPrimaryOilTanksIncomplete={isPrimaryOilTanksIncomplete}
            />
          </Box>
        </SimpleGrid>
      </SectionCard>

      <AuxiliaryHeatingModal
        isOpen={auxiliaryModal.isOpen}
        onClose={auxiliaryModal.onClose}
        location={location}
        onSave={handleAuxiliarySave}
        isUpdating={isUpdating}
        itvLockedFields={location.itv_locked_fields}
      />

      <OilTanksModal
        isOpen={oilTanksModal.isOpen}
        onClose={oilTanksModal.onClose}
        heatingSource="primary"
        oilTanks={location.oil_tanks || []}
        onSave={handleOilTanksUpdate}
        isUpdating={isUpdating}
      />
    </ValidationEntityProvider>
  );
};

export default CombinedDetailsCard;
